import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Building2,
  Users,
  Layers,
  BarChart3,
  Crown,
  RefreshCw,
} from 'lucide-react';
import { InnerLayout, PillNavItem } from '../components/layout/InnerLayout';
import { HedgehogSpinner } from '../components/brand/HedgehogSpinner';
import { Button } from '../components/ui/Button';
import { AdminOrganizationsGrid } from '../components/admin/AdminOrganizationsGrid';
import { AdminTenantsGrid } from '../components/admin/AdminTenantsGrid';
import { useAdminData } from './admin/hooks/useAdminData';
import { AdminOverviewTab } from './admin/components/AdminOverviewTab';
import { AdminQueuesTab } from './admin/components/AdminQueuesTab';

interface AdminViewProps {
  section?: 'overview' | 'tenants' | 'organizations' | 'queues';
}

export const AdminView: React.FC<AdminViewProps> = ({ section: propSection }) => {
  const location = useLocation();

  const activeSection: 'overview' | 'tenants' | 'organizations' | 'queues' = useMemo(() => {
    if (propSection) return propSection;
    const path = location.pathname;
    if (path.includes('/tenants')) return 'tenants';
    if (path.includes('/organizations')) return 'organizations';
    if (path.includes('/queues')) return 'queues';
    return 'overview';
  }, [propSection, location.pathname]);

  const {
    user,
    isLoading,
    isRefreshing,
    organizations,
    tenants,
    plans,
    telemetry,
    updatingOrgId,
    orgsPage,
    orgsPageSize,
    totalOrgs,
    orgsSearch,
    orgsSortRules,
    orgsFilterRules,
    isOrgsLoading,
    tenantsPage,
    tenantsPageSize,
    totalTenants,
    tenantsSearch,
    tenantsSortRules,
    tenantsFilterRules,
    isTenantsLoading,
    handleRefresh,
    handleOrgsPageChange,
    handleOrgsPageSizeChange,
    handleOrgsSearchChange,
    handleOrgsSortChange,
    handleOrgsFilterChange,
    handleTenantsPageChange,
    handleTenantsPageSizeChange,
    handleTenantsSearchChange,
    handleTenantsSortChange,
    handleTenantsFilterChange,
    handleTierUpgrade,
    handleUpdateOrg,
    handleDeleteOrg,
    handleBulkDeleteOrgs,
    handleDeleteTenant,
    handleBulkDeleteTenants,
  } = useAdminData(activeSection);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <HedgehogSpinner size="lg" label="LOADING PLATFORM TELEMETRY..." />
      </div>
    );
  }

  const isFullHeight = activeSection === 'organizations' || activeSection === 'tenants';

  const headerTitle =
    activeSection === 'tenants'
      ? 'Platform Users Registry'
      : activeSection === 'organizations'
      ? 'Organizations & Quota Administration'
      : activeSection === 'queues'
      ? 'Notification Queue & Worker Fleet Diagnostics'
      : 'Global Platform & System Telemetry';

  const headerDescription =
    activeSection === 'tenants'
      ? 'Search, inspect, and manage user accounts, active status, credentials, and organizations.'
      : activeSection === 'organizations'
      ? 'Manage subscription tiers, scale ingestion limits, and audit storage footprints.'
      : activeSection === 'queues'
      ? 'Real-time monitoring of PostgreSQL SKIP LOCKED dispatch engine, worker lag, and failure recovery.'
      : 'Real-time platform observability, queue throughput, and subscription tier adjustments.';

  const adminNavItems: PillNavItem[] = [
    {
      id: 'overview',
      label: 'Platform Overview',
      icon: BarChart3,
      active: activeSection === 'overview',
      href: '/admin',
    },
    {
      id: 'organizations',
      label: `Organizations (${totalOrgs || organizations.length})`,
      icon: Building2,
      active: activeSection === 'organizations',
      href: '/admin/organizations',
    },
    {
      id: 'tenants',
      label: `Users & Tenants (${totalTenants || tenants.length})`,
      icon: Users,
      active: activeSection === 'tenants',
      href: '/admin/tenants',
    },
    {
      id: 'queues',
      label: 'Queues & Workers',
      icon: Layers,
      active: activeSection === 'queues',
      href: '/admin/queues',
    },
    {
      id: 'plans',
      label: 'Plans & Pricing',
      icon: Crown,
      active: false,
      href: '/admin/plans',
    },
  ];

  return (
    <InnerLayout
      header={{
        title: headerTitle,
        description: headerDescription,
        actions: !isFullHeight
          ? [
              {
                label: isRefreshing ? 'Refreshing...' : 'Refresh Telemetry',
                icon: <RefreshCw size={14} className={isRefreshing ? 'animate-spin text-cf-orange' : ''} />,
                variant: 'secondary',
                disabled: isRefreshing,
                onClick: handleRefresh,
              },
            ]
          : undefined,
      }}
      navItems={adminNavItems}
      variant={isFullHeight ? 'fullHeight' : 'default'}
    >
      {/* Platform Overview Section */}
      {activeSection === 'overview' && (
        <AdminOverviewTab
          telemetry={telemetry}
          totalTenants={totalTenants}
          totalOrgs={totalOrgs}
        />
      )}

      {/* Queue Details & Diagnostics Section */}
      {activeSection === 'queues' && (
        <AdminQueuesTab telemetry={telemetry} />
      )}

      {/* Users Data Grid (Shown on Users) */}
      {activeSection === 'tenants' && (
        <div className="flex-1 min-h-0 min-w-0 w-full max-w-full overflow-hidden flex flex-col pt-1">
          <AdminTenantsGrid
            tenants={tenants}
            totalRows={totalTenants}
            page={tenantsPage}
            pageSize={tenantsPageSize}
            onPageChange={handleTenantsPageChange}
            onPageSizeChange={handleTenantsPageSizeChange}
            onSearchChange={handleTenantsSearchChange}
            onSortChange={handleTenantsSortChange}
            sortRules={tenantsSortRules}
            onFilterChange={handleTenantsFilterChange}
            filterRules={tenantsFilterRules}
            searchValue={tenantsSearch}
            isLoading={isTenantsLoading}
            onDeleteTenant={handleDeleteTenant}
            onBulkDeleteTenants={handleBulkDeleteTenants}
            currentUserId={user?.id}
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
            persistenceKey="admin_tenants"
          />
        </div>
      )}

      {/* Organizations Data Grid (Shown on Organizations) */}
      {activeSection === 'organizations' && (
        <div className="flex-1 min-h-0 min-w-0 w-full max-w-full overflow-hidden flex flex-col pt-1">
          <AdminOrganizationsGrid
            organizations={organizations}
            plans={plans}
            totalRows={totalOrgs}
            page={orgsPage}
            pageSize={orgsPageSize}
            onPageChange={handleOrgsPageChange}
            onPageSizeChange={handleOrgsPageSizeChange}
            onSearchChange={handleOrgsSearchChange}
            onSortChange={handleOrgsSortChange}
            sortRules={orgsSortRules}
            onFilterChange={handleOrgsFilterChange}
            filterRules={orgsFilterRules}
            searchValue={orgsSearch}
            isLoading={isOrgsLoading}
            updatingOrgId={updatingOrgId}
            onTierUpgrade={handleTierUpgrade}
            onUpdateOrg={handleUpdateOrg}
            onDeleteOrg={handleDeleteOrg}
            onBulkDeleteOrgs={handleBulkDeleteOrgs}
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
            persistenceKey="admin_organizations"
          />
        </div>
      )}
    </InnerLayout>
  );
};
